/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import { CheckIcon } from '@heroicons/react/20/solid'
import { Column } from '@tanstack/react-table'

interface UiTableColumnVisibilityItemsProps<TData> {
    columns: Column<TData, unknown>[]
    draftVisibility: Record<string, boolean>
    orderedColumnIds: string[]
    getColumnLabel: (column: Column<TData, unknown>) => string
    onToggleVisibility: (columnId: string) => void
    onColumnOrderChange?: (order: string[]) => void
    scrollContainerRef?: React.RefObject<HTMLDivElement>
}

const UiTableColumnVisibilityItems = <TData,>(
    props: UiTableColumnVisibilityItemsProps<TData>
) => {
    const allowedColumns = props.columns
    const [draggedColumnId, setDraggedColumnId] = React.useState<string | null>(null)
    const [dropTarget, setDropTarget] = React.useState<{ id: string; position: 'before' | 'after' } | null>(null)

    const maybeAutoScroll = (clientY: number) => {
        const container = props.scrollContainerRef?.current
        if (!container) {
            return
        }

        const rect = container.getBoundingClientRect()
        const edgeThreshold = 36
        const scrollStep = 10

        if (clientY < rect.top + edgeThreshold) {
            container.scrollTo({ top: container.scrollTop - scrollStep })
            return
        }

        if (clientY > rect.bottom - edgeThreshold) {
            container.scrollTo({ top: container.scrollTop + scrollStep })
        }
    }

    const columnsById = React.useMemo(() => {
        const entries = allowedColumns.map((column) => [column.id, column] as const)
        return new Map(entries)
    }, [allowedColumns])

    if (!props.columns.length) {
        return null
    }

    const orderedColumns = props.orderedColumnIds
        .map((columnId) => columnsById.get(columnId))
        .filter((column): column is Column<TData, unknown> => Boolean(column))

    const moveColumn = (targetColumnId: string, position: 'before' | 'after') => {
        if (!draggedColumnId || draggedColumnId === targetColumnId) {
            return
        }

        const previousIds = [...props.orderedColumnIds]
            const fromIndex = previousIds.indexOf(draggedColumnId)
            const toIndex = previousIds.indexOf(targetColumnId)

            if (fromIndex === -1 || toIndex === -1) {
                return
            }

            const nextIds = [...previousIds]
            const [moved] = nextIds.splice(fromIndex, 1)
            const adjustedTargetIndex = position === 'after' ? toIndex + (fromIndex < toIndex ? 0 : 1) : toIndex - (fromIndex < toIndex ? 1 : 0)
            const boundedIndex = Math.max(0, Math.min(nextIds.length, adjustedTargetIndex))
            nextIds.splice(boundedIndex, 0, moved)
            props.onColumnOrderChange?.(nextIds)
    }

    return (
        <div className="UiTableColumnVisibilityItems">
            {orderedColumns.map((column) => {
                const label = props.getColumnLabel(column)

                return (
                    <div
                        key={column.id}
                        draggable
                        onDragStart={() => setDraggedColumnId(column.id)}
                        onDragOver={(event) => {
                            event.preventDefault()
                            maybeAutoScroll(event.clientY)
                            const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect()
                            const position = event.clientY - rect.top < rect.height / 2 ? 'before' : 'after'
                            setDropTarget({ id: column.id, position })
                        }}
                        onDrop={(event) => {
                            event.preventDefault()
                            const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect()
                            const position = event.clientY - rect.top < rect.height / 2 ? 'before' : 'after'
                            moveColumn(column.id, position)
                            setDraggedColumnId(null)
                            setDropTarget(null)
                        }}
                        onDragEnd={() => {
                            setDraggedColumnId(null)
                            setDropTarget(null)
                        }}
                        className="group relative"
                    >
                        {dropTarget?.id === column.id && dropTarget.position === 'before' && (
                            <div className="absolute left-2 right-2 top-0 h-0.5 bg-blue-500 rounded" />
                        )}
                        <button
                            type="button"
                            onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                props.onToggleVisibility(column.id)
                            }}
                            className={clsx(
                                'flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100',
                                draggedColumnId === column.id && 'opacity-70'
                            )}
                        >
                            <div
                                className={clsx(
                                    'mr-2 flex h-4 w-4 items-center justify-center rounded border',
                                    (typeof props.draftVisibility[column.id] === 'boolean'
                                        ? props.draftVisibility[column.id]
                                        : column.getIsVisible())
                                        ? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                                        : 'border-gray-300 dark:border-gray-600'
                                )}
                            >
                                {(typeof props.draftVisibility[column.id] === 'boolean'
                                    ? props.draftVisibility[column.id]
                                    : column.getIsVisible()) && (
                                    <CheckIcon className="h-3 w-3 text-white" />
                                )}
                            </div>
                            <span className="capitalize flex-1 truncate">{label}</span>
                            <span className="ml-2 text-gray-400 dark:text-gray-500 cursor-grab">::</span>
                        </button>
                        {dropTarget?.id === column.id && dropTarget.position === 'after' && (
                            <div className="absolute left-2 right-2 bottom-0 h-0.5 bg-blue-500 rounded" />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default UiTableColumnVisibilityItems
